/** Pure twin of ft3d_wall_climb.php geometry; oracle: mv12-wall-climb-geometry-oracle.json. */
import { roofDeckBoxes, type RoofDeckBox } from '../../encounter-creation-engine/floor-band.js';

export type WallClimbDirection = 'up' | 'down' | 'hold' | 'over';
export type WallClimbPlan = Omit<import('../../encounter-creation-engine/wall-climb.js').WallClimbReceipt, 'wallRunId' | 'over' | 'sceneWall'> & {
  wallRunId?: string; over?: boolean; sceneWall?: boolean;
  path: Point[]; foot: Point | null; links: ClimbLink[]; ledgeFt?: number | null;
};
export type WallClimbGeometry = { ok: true; plan: WallClimbPlan } | { ok: false; refusal: string };
type Point = [number, number, number];
type Bounds = { min?: unknown; max?: unknown };
type Opening = { plane?: { axis?: unknown; at?: unknown }; span?: Bounds; z?: Bounds };
type WallRun = { axis?: unknown; at?: unknown; span?: Bounds };
type SceneWall = WallRun & { thicknessFt?: unknown; z?: Bounds; openings?: readonly unknown[] };
type Shape = Parameters<typeof roofDeckBoxes>[0] & { openings?: readonly unknown[] };
type ClimbLink = { kind: 'climb'; x: number; y: number; from_z: number; to_z: number };

// PHP casts nonnumeric strings to zero and numeric prefixes to their float value.
function float(value: unknown): number {
  if (typeof value === 'string') return Number(value.match(/^\s*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/)?.[0] ?? 0);
  if (value !== null && typeof value === 'object') return Object.keys(value).length ? 1 : 0;
  return Number(value ?? 0);
}
const empty = (value: unknown): boolean => value == null || value === false || value === 0 || value === '' || value === '0'
  || (typeof value === 'object' && Object.keys(value).length === 0);
const isOpening = (value: unknown): value is Opening => value !== null && typeof value === 'object';
class GeometryRefusal extends Error {}
function refuse(message: string): never { throw new GeometryRefusal(message); }
function geometry(run: () => Record<string, unknown>): WallClimbGeometry {
  try { return { ok: true, plan: run() as WallClimbPlan }; }
  catch (error: unknown) {
    if (error instanceof GeometryRefusal) return { ok: false, refusal: error.message };
    throw error;
  }
}

export function wallClimbGeometry(shape: Shape, wallRun: WallRun, roomId: string, origin: Point, radius: number,
  size: number, direction: string, holdZ: number | null = null): WallClimbGeometry {
  return geometry(() => {
    const axis = wallRun.axis ?? '';
    if ((axis !== 'x' && axis !== 'y') || !['up', 'down', 'hold'].includes(direction)) refuse('That wall cannot be climbed.');
    const n = axis === 'x' ? 0 : 1, t = n === 0 ? 1 : 0;
    const at = float(wallRun.at ?? NaN), spanMin = float(wallRun.span?.min ?? NaN), spanMax = float(wallRun.span?.max ?? NaN);
    if (![at, spanMin, spanMax].every(Number.isFinite) || spanMax <= spanMin) refuse('That wall cannot be climbed.');
    const boxes = (shape.boxes ?? []).filter(b => b && typeof b === 'object' && empty(b.roofDeck)
      && b.navigationOpenGround !== true && (b.creation?.enclosure ?? 'none') !== 'none');
    let room: (typeof boxes)[number] | null = null;
    for (const b of boxes) if (String(b.id ?? '') === roomId) room = b;
    if (room === null) refuse('That wall cannot be climbed.');
    const out = at >= (float(room[axis].min) + float(room[axis].max)) / 2 ? 1 : -1;
    const half = (spanMax - spanMin) / 2;
    const tangent = Math.max(spanMin + Math.min(radius, half), Math.min(spanMax - Math.min(radius, half), float(origin[t])));
    const probe: [number, number] = [0, 0];
    probe[n] = at - out * .25; probe[t] = tangent;
    const faceKey = out > 0 ? 'max' : 'min';
    const reaches = (b: (typeof boxes)[number]): boolean => probe[0] >= float(b.x.min) && probe[0] <= float(b.x.max)
      && probe[1] >= float(b.y.min) && probe[1] <= float(b.y.max);
    const stacked = (from: (typeof boxes)[number], upward: boolean): (typeof boxes)[number] | null => {
      for (const b of boxes) {
        if (b === from || !reaches(b) || Math.abs(float(b[axis][faceKey]) - at) > .01) continue;
        if (upward ? Math.abs(float(b.floor) - float(from.ceiling)) <= .01 : Math.abs(float(b.ceiling) - float(from.floor)) <= .01) return b;
      }
      return null;
    };
    let bottom = room, top = room;
    let next: (typeof boxes)[number] | null;
    while ((next = stacked(bottom, false)) !== null) bottom = next;
    while ((next = stacked(top, true)) !== null) top = next;
    const baseZ = float(bottom.floor), level = float(top.creation?.physicalCeilingFt ?? top.ceiling);
    const outside: Point = [0, 0, 0];
    outside[n] = at + out * (radius + .35); outside[t] = tangent;
    let ledge: Point | null = [0, 0, level];
    ledge[n] = at - out * (radius + .35); ledge[t] = tangent;
    let ridge: RoofDeckBox | null = null;
    for (const d of roofDeckBoxes(shape)) if (!empty(d.ridge) && (d.roomIds ?? []).includes(String(top.id ?? ''))) ridge = d;
    if (ridge !== null) {
      const ridgeX = (ridge.x.min + ridge.x.max) / 2, ridgeHalf = (float(ridge.y.max) - float(ridge.y.min)) / 2;
      ledge = axis === 'x' || !empty(ridge.hipped)
        ? [ridgeX, Math.max(float(ridge.y.min) + Math.min(radius, ridgeHalf),
          Math.min(float(ridge.y.max) - Math.min(radius, ridgeHalf), axis === 'x' ? tangent : at)), float(ridge.floor)] : null;
    }
    const ledgeZ = ledge?.[2] ?? level;
    if (wallOpeningAt(shape, axis, at, tangent, radius, baseZ)) refuse('There is an opening in this wall here. Go through it instead.');
    let deck: RoofDeckBox | null = null;
    if (ridge !== null) deck = ledge !== null ? ridge : null;
    else {
      for (const d of roofDeckBoxes(shape)) {
        if (!empty(d.ridge) || Math.abs(float(d.floor) - level) > .01 || d.roofOf !== String(top.id ?? '')) continue;
        if (ledge !== null && ledge[0] - radius >= float(d.x.min) - 1e-6 && ledge[0] + radius <= float(d.x.max) + 1e-6
          && ledge[1] - radius >= float(d.y.min) - 1e-6 && ledge[1] + radius <= float(d.y.max) + 1e-6) { deck = d; break; }
      }
    }
    const noLedge = (): never => refuse(ridge !== null
      ? 'The roof comes down to a gable here. Climb a wall under the eaves to reach the ridge.'
      : 'There is no ledge to stand on at the top of this wall.');
    const reach = Math.max(5, size / 2 + .35) + .01;
    origin = [float(origin[0]), float(origin[1]), float(origin[2])];
    const standoff = radius + .35;
    const onFace = Math.abs((origin[n] - at) * out - standoff) <= .3 && Math.abs(origin[t] - tangent) <= .3
      && origin[2] > baseZ + .05 && origin[2] < level - .05;
    const onDeck = deck !== null && Math.abs(origin[2] - ledgeZ) <= .05 && (origin[n] - at) * out < -.05
      && origin[0] >= float(deck.x.min) - 1e-6 && origin[0] <= float(deck.x.max) + 1e-6
      && origin[1] >= float(deck.y.min) - 1e-6 && origin[1] <= float(deck.y.max) + 1e-6;
    if (onFace) { outside[n] = origin[n]; outside[t] = origin[t]; }
    const faceAt = (z: number): Point => [outside[0], outside[1], z];
    const fromGround = (): void => {
      if ((origin[n] - at) * out <= .05) refuse('Climb this wall from outside the building.');
      for (const b of boxes) {
        if (origin[0] > float(b.x.min) && origin[0] < float(b.x.max) && origin[1] > float(b.y.min) && origin[1] < float(b.y.max)
          && origin[2] >= float(b.floor) - .01 && origin[2] < float(b.ceiling) - .01) refuse('Climb this wall from outside the building.');
      }
      if (Math.abs(origin[2] - baseZ) > Math.max(2.5, size) + .01) refuse('The foot of this wall is out of reach from this elevation.');
      if (Math.hypot(origin[0] - outside[0], origin[1] - outside[1]) > reach) refuse('Move next to the wall first.');
    };
    const fromDeck = (): void => {
      const edge = ledge ?? noLedge();
      const gap = ridge !== null && axis === 'x' ? Math.abs(origin[1] - edge[1]) : Math.hypot(origin[0] - edge[0], origin[1] - edge[1]);
      if (gap > reach) refuse('Move to the edge above this wall first.');
    };
    if (direction === 'hold') {
      if (holdZ === null || !Number.isFinite(holdZ) || holdZ < baseZ + 1 - 1e-6 || holdZ > level - 1 + 1e-6) refuse('Choose a height on the face of this wall.');
      if (onFace && Math.abs(holdZ - origin[2]) < .05) refuse('You are already holding on at that height.');
      if (wallOpeningAt(shape, axis, at, tangent, radius, holdZ)) refuse('There is an opening in the wall at that height.');
    }
    if (direction === 'up' && deck === null) noLedge();
    const toZ = direction === 'up' ? level : direction === 'down' ? baseZ : float(holdZ);
    let path: Point[], fromZ: number;
    if (onFace) {
      path = direction === 'up' ? [origin, faceAt(level), ledge!] : [origin, faceAt(toZ)];
      fromZ = origin[2];
    } else if (direction === 'down' || (direction === 'hold' && onDeck)) {
      if (deck === null) noLedge();
      if (!onDeck) refuse('Stand on the roof above this wall first.');
      fromDeck();
      path = [origin, ledge!, faceAt(level), faceAt(toZ)]; fromZ = level;
    } else {
      fromGround();
      const foot = faceAt(origin[2]);
      path = direction === 'up' ? [origin, foot, faceAt(level), ledge!] : [origin, foot, faceAt(toZ)]; fromZ = origin[2];
    }
    const foot = path[1]![2] === origin[2] && !onFace && !onDeck ? path[1]! : direction === 'down' ? path[path.length - 1]! : null;
    return {
      kind: 'wall-climb', direction, path, axis, at, baseFt: baseZ, topFt: level,
      faceFt: { x: outside[0], y: outside[1] }, inward: { x: n === 0 ? -out : 0, y: n === 1 ? -out : 0 }, foot,
      holdFt: direction === 'hold' ? toZ : null,
      deck: {
        minX: n === 0 && out < 0 ? outside[0] : float(deck?.x.min ?? outside[0]),
        maxX: n === 0 && out > 0 ? outside[0] : float(deck?.x.max ?? outside[0]),
        minY: n === 1 && out < 0 ? outside[1] : float(deck?.y.min ?? outside[1]),
        maxY: n === 1 && out > 0 ? outside[1] : float(deck?.y.max ?? outside[1]),
      },
      ledgeFt: ledge !== null ? ledgeZ : null,
      links: [{ kind: 'climb', x: outside[0], y: outside[1], from_z: fromZ, to_z: toZ }],
    };
  });
}

export function wallOpeningAt(shape: Shape, axis: string, at: number, tangent: number, radius: number, z: number): boolean {
  for (const opening of shape.openings ?? []) {
    if (!isOpening(opening) || (opening.plane?.axis ?? '') !== axis || Math.abs(float(opening.plane?.at ?? Infinity) - at) > .01) continue;
    if (float(opening.span?.min ?? Infinity) < tangent + radius && float(opening.span?.max ?? -Infinity) > tangent - radius
      && float(opening.z?.min ?? Infinity) <= z + .5 && float(opening.z?.max ?? -Infinity) > z + .5) return true;
  }
  return false;
}

export function sceneWallClimbGeometry(wall: SceneWall, origin: Point, radius: number, size: number, direction: string,
  holdZ: number | null = null, side = 0): WallClimbGeometry {
  return geometry(() => {
    const axis = wall.axis;
    if ((axis !== 'x' && axis !== 'y') || !['over', 'up', 'hold', 'down'].includes(direction)) refuse('That wall cannot be climbed.');
    const n = axis === 'x' ? 0 : 1, t = n === 0 ? 1 : 0;
    const at = float(wall.at), half = Math.max(.05, float(wall.thicknessFt ?? .5)) / 2;
    const base = float(wall.z?.min), top = float(wall.z?.max), spanMin = float(wall.span?.min), spanMax = float(wall.span?.max);
    origin = [float(origin[0]), float(origin[1]), float(origin[2])];
    const tangent = Math.max(spanMin + Math.min(radius, (spanMax - spanMin) / 2), Math.min(spanMax - Math.min(radius, (spanMax - spanMin) / 2), origin[t]));
    const standoff = half + radius + .35, offset = origin[n] - at;
    let point = (normal: number, z: number): Point => { const p: Point = [0, 0, z]; p[n] = normal; p[t] = tangent; return p; };
    const inSpan = origin[t] >= spanMin - .01 && origin[t] <= spanMax + .01;
    const onTop = inSpan && Math.abs(origin[2] - top) <= .05 && Math.abs(offset) <= half + 1e-6;
    const onFace = !onTop && inSpan && Math.abs(Math.abs(offset) - standoff) <= .05 && origin[2] > base + .05 && origin[2] < top - .05;
    const here = offset >= 0 ? 1 : -1;
    if (onFace) point = (normal: number, z: number): Point => { const p: Point = [0, 0, z]; p[n] = normal; p[t] = origin[t]; return p; };
    const wide = 2 * half >= 2 && 2 * half >= 2 * radius - 1e-6;
    if (direction === 'hold' && (holdZ === null || holdZ < base + 1 - 1e-6 || holdZ > top - 1 + 1e-6)) refuse('Choose a height on the face of this wall.');
    const near = at + here * standoff, far = at - here * standoff;
    const links: ClimbLink[] = [];
    const link = (a: Point, b: Point): void => { links.push({ kind: 'climb', x: a[0], y: a[1], from_z: a[2], to_z: b[2] }); };
    let path: Point[], foot: Point | null;
    if (onTop) {
      const to = side === 0 ? 1 : side > 0 ? 1 : -1;
      if (direction === 'up' || direction === 'over') refuse('You are already on top of this wall.');
      const edge = point(at + to * standoff, top), end = point(at + to * standoff, direction === 'hold' ? float(holdZ) : base);
      path = [origin, edge, end]; link(edge, end); foot = direction === 'down' ? end : null;
    } else if (onFace) {
      if (direction === 'up' && !wide) refuse('The top of this wall is too narrow to stand on. Climb over it instead.');
      if (direction === 'hold' && Math.abs(float(holdZ) - origin[2]) < .05) refuse('You are already holding on at that height.');
      const faceTop = point(origin[n], top);
      if (direction === 'over') {
        const farTop = point(far, top), farFoot = point(far, base);
        path = [origin, faceTop, farTop, farFoot]; link(origin, faceTop); link(farTop, farFoot); foot = farFoot;
      } else if (direction === 'up') {
        path = [origin, faceTop, point(at, top)]; link(origin, faceTop); foot = null;
      } else {
        const end = point(origin[n], direction === 'hold' ? float(holdZ) : base);
        path = [origin, end]; link(origin, end); foot = direction === 'down' ? end : null;
      }
    } else {
      if (direction === 'down') refuse('You are not on this wall.');
      if (Math.abs(offset) <= half + radius) refuse('Step back from the wall first.');
      if (Math.abs(origin[2] - base) > Math.max(2.5, size) + .01) refuse('The foot of this wall is out of reach from this elevation.');
      const footHere = point(near, origin[2]);
      if (Math.hypot(origin[0] - footHere[0], origin[1] - footHere[1]) > Math.max(5, size / 2 + .35) + .01) refuse('Move next to the wall first.');
      if (direction === 'up' && !wide) refuse('The top of this wall is too narrow to stand on. Climb over it instead.');
      const faceTop = point(near, top);
      if (direction === 'over') {
        const farTop = point(far, top), farFoot = point(far, base);
        path = [origin, footHere, faceTop, farTop, farFoot]; link(footHere, faceTop); link(farTop, farFoot); foot = farFoot;
      } else if (direction === 'up') {
        path = [origin, footHere, faceTop, point(at, top)]; link(footHere, faceTop); foot = footHere;
      } else {
        const hang = point(near, float(holdZ)); path = [origin, footHere, hang]; link(footHere, hang); foot = footHere;
      }
    }
    const column = path[1]![t], zs = path.map(p => p[2]);
    const zLo = Math.max(base, Math.min(...zs)), zHi = Math.min(top, Math.max(...zs));
    if (sceneWallOpeningOverlapsColumn(wall, column, radius, zLo, zHi)) refuse('There is an opening in this wall here. Go through it instead.');
    const final = path[path.length - 1]!, inwardSign = onTop ? -(side >= 0 ? 1 : -1) : -here;
    return {
      kind: 'wall-climb', direction, path, axis, at, baseFt: base, topFt: top, faceFt: { x: path[1]![0], y: path[1]![1] },
      inward: { x: n === 0 ? inwardSign : 0, y: n === 1 ? inwardSign : 0 }, foot,
      holdFt: direction === 'hold' ? float(holdZ) : null, over: direction === 'over',
      deck: { minX: final[0], maxX: final[0], minY: final[1], maxY: final[1] }, links, sceneWall: true,
    };
  });
}

export function sceneWallOpeningOverlapsColumn(wall: SceneWall, tangent: number, radius: number, zLo: number, zHi: number): boolean {
  const base = float(wall.z?.min), top = float(wall.z?.max);
  for (const opening of wall.openings ?? []) {
    if (!isOpening(opening)) continue;
    const oLo = float(opening.z?.min ?? base), oHi = float(opening.z?.max ?? top);
    if (float(opening.span?.min ?? Infinity) < tangent + radius && float(opening.span?.max ?? -Infinity) > tangent - radius
      && oLo < zHi - 1e-6 && oHi > zLo + 1e-6) return true;
  }
  return false;
}
